import { Dimensions, SpreadGroundImage } from 'editor/src/store/design/types';

export interface SpreadGroundImageScale {
  scaleX: number;
  scaleY: number;
}

// How far apart the two axis scales may be before the asset is treated as
// disagreeing with its print area rather than merely rounded.
//
// Real scenes never agree exactly. The t-shirt scenes the editor is tested
// against are 0.2% to 1.5% apart (front 746x995 px for 305x406 mm, neck-inner
// 190x131 px for 70x49 mm) — whoever exported them rounded the rect to whole
// pixels. Honouring that as a genuine mismatch and scaling both axes by the
// smaller of the two shrinks the product by the same fraction, which is
// invisible to the eye but moves antialiased edges and breaks every
// pixel-perfect canvas snapshot.
//
// A contradiction that matters is an order of magnitude larger: the TR700's
// 190x250 mm all-over area shipped with the 554x930 px front-face rect is 27.6%
// apart. 5% sits well clear of both.
const ASPECT_TOLERANCE = 0.05;

// GPS-27341 — a bg/fg asset carries the print area's rect (left/top/width/height)
// in its own pixels, so the scale that maps the asset onto the canvas comes from
// that rect against the spread's mm. One scale per axis places the rect exactly
// over the spread, at the cost of stretching the photo whenever the rect's px
// aspect doesn't match the print area's mm aspect.
//
// Within the tolerance the per-axis scales stand, exactly as before — the rect is
// the print area and it should land on it. Past the tolerance the asset cannot be
// describing this print area, so the photo's own proportions win: both axes take
// the smaller scale, and the print area, drawn at its declared size, is allowed to
// extend past the region the rect marks. For a wrap area that is the honest
// picture — 190 mm of circumference does not fit on a photo of one face.
export function getSpreadGroundScales(
  image: SpreadGroundImage,
  contentDimensions: Dimensions,
  // Off whenever the content box is not the single spread the rect belongs to —
  // a grouped spread stacks several of them, so the rect is meant to be
  // stretched across the group and the mismatch says nothing about the asset.
  allowUniformScale = true,
): SpreadGroundImageScale {
  // A rect of zero is unusable as a ruler, and dividing by it would hand fabric an
  // infinite scale and blank the canvas. Fall back to the asset's own size — the
  // whole image then stands in for the print area, which at least renders.
  const rectWidth = image.width || image.imgWidth;
  const rectHeight = image.height || image.imgHeight;
  if (!rectWidth || !rectHeight) {
    return { scaleX: 1, scaleY: 1 };
  }

  const rawScaleX = contentDimensions.width / rectWidth;
  const rawScaleY = contentDimensions.height / rectHeight;
  const larger = Math.max(rawScaleX, rawScaleY);
  if (!allowUniformScale || !larger || Math.abs(rawScaleX - rawScaleY) / larger <= ASPECT_TOLERANCE) {
    return { scaleX: rawScaleX, scaleY: rawScaleY };
  }

  const uniform = Math.min(rawScaleX, rawScaleY);
  return { scaleX: uniform, scaleY: uniform };
}

export default function getSpreadGroundImageScale(
  image: SpreadGroundImage,
  spreadWidth: number,
  spreadHeight: number,
  mm2px: (size: number) => number,
  allowUniformScale = true,
): SpreadGroundImageScale {
  return getSpreadGroundScales(
    image,
    {
      width: mm2px(spreadWidth),
      height: mm2px(spreadHeight),
    },
    allowUniformScale,
  );
}
